import { useState } from "react";
import { Button, ButtonGroup, Popover, PopoverBody, Tooltip, UncontrolledTooltip } from "reactstrap";
import brush from '../styles/tools/brush.svg';
import bucket from '../styles/tools/bucket.svg';
import eraser from '../styles/tools/eraser.svg';
import pencil from '../styles/tools/pencil.svg';
import picker from '../styles/tools/picker.svg';
import scissor from '../styles/tools/scissor.svg';
import spray from '../styles/tools/spray.svg';
import trash from '../styles/tools/trash.svg';
import ColorsPicker from "./colors-picker";
import { FacebookIcon, FacebookShareButton, PinterestIcon, PinterestShareButton, TwitterIcon, TwitterShareButton, WhatsappIcon, WhatsappShareButton } from 'react-share'
import { uploadImageDataUrl } from "../firebase";
import { v4 as uuidv4 } from 'uuid';

const topShelfTools = [
  {
    name: 'Bucket',
    img: bucket
  },
  {
    name: 'Brush',
    img: brush
  },
  {
    name: 'Pencil',
    img: pencil
  },
  {
    name: 'Spray',
    img: spray
  }
]

const bottomShelfTools = [
  {
    name: 'Eraser',
    img: eraser
  },
  {
    name: 'Scissor',
    img: scissor
  },
  {
    name: 'Picker',
    img: picker
  },
  {
    name: 'Trash',
    img: trash
  }
]

function Toolbar({ onToolSelect, onZoomChange, onUndo, onRedo, onSave, onPost, undoDisabled, redoDisabled, pickedColor, onColorPick, tools, isReadyToSaveOrShare, drawingName, imageDataUrl }) {
  const [brushState, setBrushState] = useState({ size: 10, isOpen: false })
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const [isSocialMediaOpen, setIsSocialMediaOpen] = useState(false)
  const [shareUrl, setShareUrl] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)

  function brushSizeBody() {
    const brushSizes = [10, 30, 50]

    return <div id="brush-sizes">
      {brushSizes.map(size =>
        <div
          key={size}
          className={"size-circle " + (brushState.size === size ? 'active' : '')}
          style={{ width: size / 3 + 'px', height: size / 3 + 'px' }}
          onClick={() => handleBrushSizeSelect(size)}
        />
      )}
    </div>
  }

  function handleBrushSizeSelect(size) {
    onToolSelect("Brush", size)
    setBrushState({ isOpen: false, size })
  }

  function handleToolSelect(toolName) {
    onToolSelect(toolName)
    setBrushState({ ...brushState, isOpen: false })
  }

  function selectedColorStyle() {
    if (pickedColor.type === 'solid') {
      return { backgroundColor: pickedColor.color }
    }

    return {
      backgroundImage: `url(${pickedColor.color})`,
      backgroundSize: 'contain'
    }
  }

  function prepareShareUrl(imageUrl) {
    return fetch('https://us-central1-coloringclub-6a29b.cloudfunctions.net/createDynamicLink', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dynamicLinkInfo: {
          domainUriPrefix: 'https://heyartapp.page.link',
          link: window.location.href,
          socialMetaTagInfo: {
            socialTitle: drawingName,
            socialDescription: "HeyArt is an Art Club for Coloring Pages and More!",
            socialImageLink: imageUrl,
          }
        }
      })
    })
      .then(res => res.json())
      .then(result => {
        setShareUrl(result.shortLink)
      })
      .catch(console.error)
  }

  function handleShare() {
    if (isSocialMediaOpen) {
      setIsSocialMediaOpen(false)
    } else {
      uploadImageDataUrl('social' + uuidv4(), imageDataUrl).then(imageUrl => {
        setImageUrl(imageUrl)

        prepareShareUrl(imageUrl)
          .then(() => {
            setIsSocialMediaOpen(true)
          })
      })
    }
  }

  return (
    <div id="tools-shelf">
      <div className="shelf top">
        <div className="tools-cont">
          {topShelfTools.map(tool =>
            <Button
              outline
              key={tool.name}
              id={tool.name}
              className={"tool " + (tools[tool.name] ? 'active' : '')}
              onClick={() => handleToolSelect(tool.name)}
            >
              <img src={tool.img} />
            </Button>
          )}
        </div>
      </div>

      <div className="shelf bottom">
        <div className="tools-cont">
          {bottomShelfTools.map(tool =>
            <Button
              outline
              key={tool.name}
              id={tool.name}
              className={"tool " + (tools[tool.name] ? 'active' : '')}
              onClick={() => handleToolSelect(tool.name)}
            >
              <img src={tool.img} />
            </Button>
          )}
        </div>
      </div>

      <div id="color-picker-container" onClick={() => setColorPickerOpen(!colorPickerOpen)}>
        <div id="foreground"></div>
        <div id="background" style={selectedColorStyle()}></div>
      </div>

      <div id="zoom-steps-cont">
        <ButtonGroup size="sm">
          <Button color="primary" disabled={undoDisabled} outline onClick={onUndo}>
            <i className="bi bi-arrow-90deg-left"></i>
          </Button>
          <Button color="primary" outline disabled={redoDisabled} onClick={onRedo}>
            <i className="bi bi-arrow-90deg-right"></i>
          </Button>
        </ButtonGroup>
        <ButtonGroup size="sm">
          <Button outline onClick={() => onZoomChange('in')}>
            <i className="bi bi-zoom-in"></i>
          </Button>
          <Button outline onClick={() => onZoomChange('out')}>
            <i className="bi bi-zoom-out"></i>
          </Button>
          <Button outline onClick={() => onZoomChange('fs')}>
            <i className="bi bi-fullscreen"></i>
          </Button>
        </ButtonGroup>
      </div>

      <div className="mt-3">
        <Button color="success" size="sm" onClick={onSave} disabled={!isReadyToSaveOrShare}>
          <i className="bi bi-folder-plus"></i> Save
        </Button>
        <Button color="primary" className="me-2 ms-2" size="sm" onClick={onPost} disabled={!isReadyToSaveOrShare}>
          <i className="bi bi-send"></i> Post
        </Button>
        <Button color="info" size="sm" id="social-media-btn" onClick={handleShare} disabled={!isReadyToSaveOrShare}>
          <i className="bi bi-share"></i> Share
        </Button>
      </div>

      <Popover
        isOpen={brushState.isOpen}
        placement="right"
        target="Brush"
        toggle={() => setBrushState({ ...brushState, isOpen: !brushState.isOpen })}
        trigger="legacy"
      >
        <PopoverBody>{brushSizeBody()}</PopoverBody>
      </Popover>

      <Popover
        isOpen={isSocialMediaOpen}
        placement="top"
        target="social-media-btn"
        toggle={() => setIsSocialMediaOpen(!isSocialMediaOpen)}
        trigger="legacy"
      >
        <PopoverBody className="p-2">
          <div id="share-btns-cont">
            <FacebookShareButton url={shareUrl}>
              <FacebookIcon size={36} round={true} />
            </FacebookShareButton>

            <PinterestShareButton media={imageUrl} url={shareUrl}>
              <PinterestIcon size={36} round={true} />
            </PinterestShareButton>

            <TwitterShareButton url={shareUrl}>
              <TwitterIcon size={36} round={true} />
            </TwitterShareButton>

            <WhatsappShareButton url={shareUrl}>
              <WhatsappIcon size={36} round={true} />
            </WhatsappShareButton>
          </div>
        </PopoverBody>
      </Popover>

      <ColorsPicker
        isOpen={colorPickerOpen}
        targetElem="color-picker-container"
        onPick={onColorPick}
        pickedColor={pickedColor}
      />

      <UncontrolledTooltip
        popperClassName="color-picker-tooltip"
        placement="left"
        target="color-picker-container"
      >
        Pick a Color!
      </UncontrolledTooltip>
    </div>
  );
}

export default Toolbar;

